import React from 'react';
import { movdTokens } from './movdTokens.js';
import MovdLogo from '../components/MovdLogo.jsx';

export function MovdSiteHeader() {
  return <header className="movd-site-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, marginBottom: 20, minHeight: 42 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(20px, 4vw, 52px)' }}>
      <MovdLogo size="medium" />
      <nav className="movd-site-header-nav" aria-label="Primary" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(14px, 2vw, 26px)' }}>
        {[['How it works', '/how-it-works'], ['Pricing', '/pricing'], ['Moving resources', '/blog'], ['For movers', '/become-partner']].map(([label, href]) => <a key={label} href={href} style={{ color: '#173057', textDecoration: 'none', fontSize: '.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</a>)}
      </nav>
    </div>
    <div className="movd-site-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 18, flexShrink: 0 }}>
      <a className="movd-site-header-sign-in" href="/login" style={{ color: '#173057', textDecoration: 'none', fontSize: '.7rem', fontWeight: 700 }}>Sign in</a>
      <a href="/become-partner" style={{ background: '#008d7b', borderRadius: 6, color: '#fff', textDecoration: 'none', fontSize: '.68rem', fontWeight: 800, padding: '10px 15px', whiteSpace: 'nowrap' }}>Become a Partner</a>
    </div>
  </header>;
}

export function MovdJourneyProgress({ steps, currentStep }) {
  const activeIndex = Math.max(0, steps.findIndex(step => step.key === currentStep));
  return <nav className="movd-journey-progress" aria-label="Journey steps" style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(62px, 1fr))`, alignItems: 'start', margin: '0 auto 28px', maxWidth: 760, whiteSpace: 'nowrap', position: 'relative', width: '100%' }}>
    <span aria-hidden="true" style={{ position: 'absolute', height: 1, background: '#d6dfdf', top: 10, left: '5%', right: '5%' }} />
    {steps.map((step, index) => <button key={step.key} type="button" title={step.label} style={{ background: 'transparent', border: 'none', color: index <= activeIndex ? '#14786f' : '#758193', fontWeight: index === activeIndex ? movdTokens.typography.weight.extrabold : movdTokens.typography.weight.semibold, fontSize: '.57rem', padding: 0, cursor: 'default', whiteSpace: 'nowrap', position: 'relative', zIndex: 1 }} aria-current={index === activeIndex ? 'step' : undefined}><span style={{ width: 21, height: 21, margin: '0 auto 5px', borderRadius: '50%', display: 'grid', placeItems: 'center', boxSizing: 'border-box', background: index <= activeIndex ? '#14786f' : '#fff', color: index <= activeIndex ? '#fff' : '#758193', border: index <= activeIndex ? '1px solid #14786f' : '1px solid #cfd8e3', fontSize: '.58rem', lineHeight: 1 }}>{index < activeIndex ? '✓' : ''}</span><span style={{ display: 'block' }}>{step.label === 'Movers' ? 'Your options' : step.label}</span></button>)}
  </nav>;
}

// Premium, light-themed container for the customer quote journey.
// Redesigned from dark dashboard to calm, professional product experience.
// Reference: docs/design/movd-quote-flow-reference.png
export default function MovdJourneyShell({ 
  eyebrow = 'WHERE ARE YOU MOVING?', 
  tagline = 'One move. All the way through.', 
  onExit, 
  steps = [], 
  currentStep, 
  aside, 
  children 
}) {
  const hasAsideContent = aside !== null && aside !== undefined;

  return (
    <div style={{
      background: movdTokens.color.background.canvas,
      color: movdTokens.color.content.primary,
      minHeight: '100vh',
      padding: '18px clamp(18px, 4vw, 34px)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      <style>{`
        .movd-journey-aside { order: -1; }
        .movd-stage-route-grid, .movd-stage-date-grid { grid-template-columns: 1fr; }
        .movd-access-grid, .movd-review-grid { grid-template-columns: 1fr; }
        .movd-journey-progress { overflow-x: auto; scrollbar-width: none; }
        .movd-journey-progress::-webkit-scrollbar { display: none; }
        .movd-quote-stage-transition { animation: movd-stage-enter 220ms ease-out both; }
        @keyframes movd-stage-enter {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .movd-quote-stage-transition { animation: none; }
        }
        @media (max-width: 767px) {
          .movd-site-header { margin-bottom: 12px !important; }
          .movd-site-header-nav, .movd-site-header-sign-in { display: none !important; }
          .movd-site-header-actions { gap: 0 !important; }
          .movd-stage-content-editorial, .movd-stage-content-aside { grid-template-columns: 1fr !important; }
          .movd-stage-route-grid, .movd-stage-date-grid { gap: ${movdTokens.space[5]}px !important; }
          .movd-stage-route-grid > *, .movd-stage-date-grid > * { min-width: 0; }
          .movd-stage-date-controls { grid-template-columns: 1fr !important; }
          .movd-stage-trust-row { grid-template-columns: 1fr !important; }
          .movd-move-size-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .movd-inventory-featured-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .movd-stage-booking, .movd-stage-payment, .movd-stage-confirmation, .movd-stage-detail, .movd-stage-review { grid-template-columns: 1fr !important; }
          .movd-stage-booking form, .movd-stage-booking .movd-booking-fields, .movd-stage-review-content { grid-template-columns: 1fr !important; }
          .movd-stage-booking aside, .movd-stage-payment aside { position: static !important; }
          .movd-stage-results-card { grid-template-columns: 76px minmax(0, 1fr) !important; }
          .movd-stage-results-card > :last-child { grid-column: 2; text-align: left !important; }
          .movd-stage-confirmation img { min-height: 280px !important; }
        }
        @media (min-width: 1024px) {
          .movd-journey-shell {
            min-height: calc(100vh - 36px);
            padding: 0;
          }
          .movd-journey-grid {
            display: grid;
            grid-template-columns: ${hasAsideContent ? 'minmax(0, 1fr) minmax(320px, 380px)' : '1fr'};
            flex: 1;
            min-height: 0;
            gap: ${movdTokens.space[6]}px;
          }
          .movd-journey-form { 
            min-height: 0;
            overflow: visible;
            padding-right: 0;
          }
          .movd-journey-aside { 
            height: 100%; 
            max-height: 100%; 
            align-self: start; 
            position: sticky;
            top: 0;
            order: 0; 
          }
          .movd-stage-route-grid, .movd-stage-date-grid {
            grid-template-columns: minmax(0, 1fr) minmax(340px, 460px);
          }
          .movd-stage-route-grid { grid-template-columns: minmax(360px, 470px) minmax(440px, 1fr); gap: clamp(42px, 7vw, 96px); }
          .movd-access-grid { grid-template-columns: 1fr 1fr; }
          .movd-review-grid { grid-template-columns: minmax(0, 1fr) 300px; }
        }
      `}</style>
      
      <div className="movd-journey-shell" style={{ 
        maxWidth: 1180,
        margin: '0 auto', 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        flex: 1, 
        minHeight: 0 
      }}>
        
        <MovdSiteHeader />

        {/* Compact, labeled horizontal progress (human readable) */}
        {steps && steps.length > 0 && (
          <MovdJourneyProgress steps={steps} currentStep={currentStep} />
        )}

        {/* Stage context: individual stages own their display headline. */}
        {(eyebrow || tagline) && (
          <div style={{ marginBottom: movdTokens.space[3], maxWidth: 620 }}>
            {eyebrow && <div style={{ color: '#337c86', fontSize: '.62rem', fontWeight: movdTokens.typography.weight.black, letterSpacing: '.18em', textTransform: 'uppercase' }}>{eyebrow}</div>}
            {tagline && <p style={{ margin: `${movdTokens.space[1]}px 0 0`, color: movdTokens.color.content.secondary, fontSize: '.93rem', lineHeight: movdTokens.typography.lineHeight.normal }}>{tagline}</p>}
          </div>
        )}

        {/* Content grid — flexible layout */}
        <div className="movd-journey-grid" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: movdTokens.space[5], 
          flex: 1, 
          minHeight: 0 
        }}>
          {/* Main form area */}
          <div className="movd-journey-form" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'visible'
          }}>
            {children}
          </div>
          
          {/* Aside panel — only renders if content provided */}
          {hasAsideContent && (
            <div className="movd-journey-aside" style={{ 
              width: '100%', 
              minHeight: 'auto',
              background: movdTokens.color.surface.base, 
              border: `1px solid ${movdTokens.color.border.soft}`,
              borderTop: `3px solid ${movdTokens.color.brand.primary}`,
              borderRadius: movdTokens.radius.lg, 
              overflow: 'auto', 
              padding: movdTokens.space[5] 
            }}>
              {aside}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
